'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["occurrences", "day"];
import * as React from 'react';
import { styled } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { EventItem } from "../event/event-item/EventItem.mjs";
import { createModal } from "../create-modal/index.mjs";
import { isOccurrenceAllDayOrMultipleDay } from "../../utils/event-utils.mjs";
import { formatWeekDayMonthAndDayOfMonth } from "../../utils/date-utils.mjs";
import { EventDialogTrigger, useEventDialogContext } from "../event-dialog/index.mjs";
import { useEventCalendarStyledContext } from "../../../event-calendar/EventCalendarStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const MoreEventsPopoverHeader = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'MoreEventsPopoverHeader'
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1),
  borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`
}));
const MoreEventsPopoverTitle = styled(Typography, {
  name: 'MuiEventCalendar',
  slot: 'MoreEventsPopoverTitle'
})(({
  theme
}) => ({
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  color: (theme.vars || theme).palette.text.primary,
  lineHeight: 1.5,
  margin: 0
}));
const MoreEventsPopoverBody = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'MoreEventsPopoverBody'
})(({
  theme
}) => ({
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: 'fit-content',
  minWidth: 200
}));
const MoreEventsPopover = createModal({
  contextName: 'MoreEventsPopoverContext'
});
export const MoreEventsPopoverContext = MoreEventsPopover.Context;
export const useMoreEventsPopoverContext = MoreEventsPopover.useContext;
export default function MoreEventsPopoverContent(props) {
  const {
    open,
    anchor,
    occurrences,
    day,
    onClose
  } = props;

  // Context hooks
  const adapter = useAdapterContext();
  const {
    classes
  } = useEventCalendarStyledContext();
  const {
    subscribeCloseHandler
  } = useEventDialogContext();
  React.useEffect(() => {
    subscribeCloseHandler(() => {
      onClose();
    });
  }, [subscribeCloseHandler, onClose]);
  return /*#__PURE__*/_jsxs(Popover, {
    className: classes.moreEventsPopover,
    open: open,
    anchorEl: anchor,
    onClose: onClose,
    children: [/*#__PURE__*/_jsx(MoreEventsPopoverHeader, {
      className: classes.moreEventsPopoverHeader,
      id: `PopoverHeader-${day.key}`,
      "aria-label": `${formatWeekDayMonthAndDayOfMonth(day.value, adapter)}`,
      children: /*#__PURE__*/_jsx(MoreEventsPopoverTitle, {
        className: classes.moreEventsPopoverTitle,
        children: formatWeekDayMonthAndDayOfMonth(day.value, adapter)
      })
    }), /*#__PURE__*/_jsx(MoreEventsPopoverBody, {
      className: classes.moreEventsPopoverBody,
      children: occurrences.map(occurrence => /*#__PURE__*/_jsx(EventDialogTrigger, {
        occurrence: occurrence,
        children: /*#__PURE__*/_jsx(EventItem, {
          variant: isOccurrenceAllDayOrMultipleDay(occurrence, adapter) ? 'filled' : 'compact',
          occurrence: occurrence,
          date: day,
          ariaLabelledBy: `PopoverHeader-${day.key}`
        })
      }, occurrence.key))
    })]
  });
}
export function MoreEventsPopoverProvider(props) {
  const {
    children
  } = props;
  return /*#__PURE__*/_jsx(MoreEventsPopover.Provider, {
    render: ({
      isOpen,
      anchorRef,
      data,
      onClose
    }) => /*#__PURE__*/_jsx(MoreEventsPopoverContent, {
      open: isOpen,
      anchor: anchorRef.current,
      occurrences: data.occurrences,
      count: data.count,
      day: data.day,
      onClose: onClose
    }),
    children: children
  });
}
export function MoreEventsPopoverTrigger(props) {
  const {
      occurrences,
      day
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const ref = React.useRef(null);
  return /*#__PURE__*/_jsx(MoreEventsPopover.Trigger, _extends({
    ref: ref,
    data: {
      occurrences,
      count: occurrences.length,
      day
    }
  }, other));
}